import { useTranslation } from "@i18n";
import { useState } from "react";
import { createTakeoff, unitTypesList } from "@Actions/estimation";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_SUCCESS } from "@Services/constants";
import Text from "@CubeComponents/html/Text/Text";
import type { CreateTakeoffRequest } from "@ServerTypes/takeoffs/takeoffs.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/** The editable takeoff fields this modal maintains; `type` is the server's measurement dimension. */
type TakeoffDraft = { name: string; description?: string; type: CreateTakeoffRequest["type"] };

/**
 * The dimensions the create/update call accepts. Keyed by the request union so that gaining or
 * losing a member is a compile error here, instead of a bad request at runtime.
 */
const TAKEOFF_DIMENSIONS: Record<TakeoffDraft["type"], true> = {
  Linear: true,
  Area: true,
  Volume: true,
  Count: true,
  Angle: true,
};

/** `unitTypesList` declares its `name` as a plain `string`, so the picked option is checked here. */
const isTakeoffDimension = (name: string | undefined): name is TakeoffDraft["type"] =>
  name !== undefined && Object.hasOwn(TAKEOFF_DIMENSIONS, name);

type CreateNewTakeoffModalProps = {
  estimateId: ObjectIdString;
  /** Also wired straight to the cancel button, so it is called both bare and with `{ refresh }`. */
  closeTakeoffModal: (args?: unknown) => void;
  editData?: TakeoffDraft | null;
  /** Supplied by the takeoffs list when editing; declared bivariantly, as each caller has its own row shape. */
  updateTakeOffData?(data: TakeoffDraft): void | Promise<void>;
  createCallback?: () => void;
};

function CreateNewTakeoffModal({
  estimateId,
  closeTakeoffModal,
  editData,
  updateTakeOffData,
  createCallback,
}: CreateNewTakeoffModalProps) {
  const { t } = useTranslation();
  const [buttonLoading, setButtonLoading] = useState(false);
  const initialTakeoffData: TakeoffDraft = editData || { name: "", description: "", type: "Count" };
  const [newTakeoffDetails, setNewTakeoffDetails] = useState<TakeoffDraft>(initialTakeoffData);
  const dispatch = useDispatch();

  const createNewTakeoff = async (event: React.MouseEvent) => {
    setButtonLoading((prev) => !prev);
    event.preventDefault();
    newTakeoffDetails.name = newTakeoffDetails.name.trim();
    newTakeoffDetails.description = newTakeoffDetails.description?.trim();
    const { name, description, type } = newTakeoffDetails;
    if (!name) return;

    if (editData) {
      // Only rendered with `updateTakeOffData` supplied whenever `editData` is set.
      await updateTakeOffData!({ ...newTakeoffDetails });
    } else {
      const response = await createTakeoff({
        name,
        description,
        type,
        estimateId,
      });
      if (response.data.status) {
        dispatch(showToastNotification({ content: t("create_takeoff", { name }), type: TOAST_TYPE_SUCCESS }));
        if (createCallback) {
          createCallback();
        }
      }
    }
    setButtonLoading((prev) => !prev);
    closeTakeoffModal({ refresh: true });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTakeoffDetails((prev) => ({ ...prev, name: e.target.value }));
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTakeoffDetails((prev) => ({ ...prev, description: e.target.value }));
  };

  const handleTypeChange = (name: string | undefined) => {
    // Clearing the field leaves the previous dimension in place: `type` is required by the
    // create/update call, so there is no "no dimension" value to fall back to.
    if (!isTakeoffDimension(name)) return;
    setNewTakeoffDetails((prev) => ({ ...prev, type: name }));
  };

  const header = () => (editData ? t("Edit Takeoff") : t("Create New Takeoff"));

  const content = () => (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3">
        <Text>{t("Takeoff Name")}</Text>
        <TextFieldComponent
          value={newTakeoffDetails.name}
          placeholder={t("Enter Takeoff Name")}
          onChange={handleNameChange}
        />
      </div>

      {Object.hasOwn(initialTakeoffData, "description") && (
        <div className="flex flex-col gap-3">
          <Text>{t("Takeoff Description")}</Text>
          <TextFieldComponent
            value={newTakeoffDetails.description}
            placeholder={t("Enter Takeoff Description")}
            onChange={handleDescriptionChange}
          />
        </div>
      )}
      {Object.hasOwn(initialTakeoffData, "type") && (
        <div className="flex flex-col gap-3">
          <Text>{t("Takeoff Type")}</Text>
          <AutoComplete
            options={unitTypesList}
            valueField="name"
            getOptionLabel={(option) => option.name || ""}
            value={unitTypesList.find((opt) => opt.name === newTakeoffDetails.type) || null}
            onChange={(_event, newValue) => {
              handleTypeChange(newValue?.name);
            }}
            slotProps={{ popper: { sx: { zIndex: "3001 !important" } }, listbox: { sx: { padding: 0 } } }}
            renderInput={(params) => <TextFieldComponent {...params} placeholder={t("Takeoff Type")} />}
          />
        </div>
      )}
    </div>
  );

  const footer = () => (
    <div className="flex items-center gap-5">
      <ButtonComponent
        disabled={!newTakeoffDetails.name.trim()}
        onClick={createNewTakeoff}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        loading={buttonLoading}
      >
        <Text>{editData ? t("Save") : t("Create")}</Text>
      </ButtonComponent>

      <ButtonComponent onClick={closeTakeoffModal} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  return (
    <ModalStateDialogComponent
      width="28.125rem"
      isVisible
      headerTitle={header()}
      zindex={3000}
      onCancel={closeTakeoffModal}
      modelContent={content()}
      footerContent={footer()}
    />
  );
}

export default CreateNewTakeoffModal;
